import React, { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { toMin } from "../../global/utility/statisticsUtil";
import {
  CHART_FONT_FAMILY,
  TOOLTIP_STYLE,
  chartTextStyle,
  buildChartTitle,
  buildLegend,
  buildSliderDataZoom,
  SLIDER_LABEL_EVENTS,
  CHART_GRID_BOTTOM,
  buildYAxisNameGap,
  formatAxisValue,
  createChartToolbox,
  CHART_GRID_RIGHT_PADDING,
} from "./ApacheChartUtils";

const ApacheScatterChart = ({
  title = "",
  xAxisData = [],
  tooltipXAxisData = [],
  series = [],
  height = "400px",
  loading = false,
  xisData = {},
}) => {
  const chartRef = useRef(null);
  const option = useMemo(() => {
    const maxVal = series.reduce((max, s) => {
      const vals = (s?.data ?? []).filter((v) => v != null);
      return vals.length > 0 ? Math.max(max, Math.max(...vals)) : max;
    }, 0);
    const yAxisNameGap = buildYAxisNameGap(maxVal, xisData?.toolTipUnit);

    return {
      textStyle: chartTextStyle,
      title: buildChartTitle(title),
      tooltip: {
        trigger: "item",
        axisPointer: { type: "none" },
        textStyle: { ...chartTextStyle, color: "#000000" },
        formatter: (params) => {
          const idx = Array.isArray(params.data) ? params.data[0] : params.dataIndex;
          const label = tooltipXAxisData[idx] ?? xAxisData[idx] ?? params.name;
          const rawVal = params.value[1] ?? params.value;
          const isMinutes = xisData?.toolTipUnit === " minute(s)";
          const formatValue = (val) =>
            isMinutes ? toMin(val) : `${val}${xisData?.toolTipUnit ? xisData.toolTipUnit : ""}`;
          return `<div style="${TOOLTIP_STYLE}">${label}</div><div style="${TOOLTIP_STYLE}">${params.marker}${params.seriesName}: <b>${formatValue(rawVal)}</b></div>`;
        },
      },
      legend: buildLegend(true),
      toolbox: createChartToolbox(chartRef),
      grid: {
        left: "5%",
        right: CHART_GRID_RIGHT_PADDING,
        bottom: CHART_GRID_BOTTOM,
        top: title ? 60 : 40,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxisData,
        name: xisData?.xName || "",
        nameLocation: "middle",
        nameGap: 25,
        nameTextStyle: { fontWeight: "bold" },
        boundaryGap: true,
        axisLabel: { rotate: 0, fontFamily: CHART_FONT_FAMILY, fontSize: 12 },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        name: xisData?.yName || "",
        nameLocation: "middle",
        nameGap: yAxisNameGap,
        nameRotate: 90,
        nameTextStyle: { fontWeight: "bold" },
        axisLabel: {
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          formatter: (val) => formatAxisValue(val, xisData?.toolTipUnit),
        },
        splitLine: { show: true },
      },
      dataZoom: buildSliderDataZoom(0, "x"),
      series: series.map((s) => ({
        name: s?.name,
        type: "scatter",
        data: s?.data,
        symbolSize: s?.symbolSize ?? 16,
        color: s?.color ?? "#0089EC",
        itemStyle: { opacity: 1 },
        emphasis: { focus: "series" },
      })),
    };
  }, [title, xAxisData, tooltipXAxisData, series, xisData]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height }}
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={SLIDER_LABEL_EVENTS}
    />
  );
};

export default ApacheScatterChart;
